import type { Gift, IncludedItem, Review, FaqItem, RichLine } from '../types/content'

/** Destination for every "Add to cart" action on the page. */
export const checkoutUrl = 'https://myeasyguide.online/cart/49902595670258:1'

export const images = {
  heroBox: 'https://cdn.magicpatterns.com/uploads/fQHr4u8atVDkW7NQQueQPW/kiitt.png',
  bottleRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/839b3536-4406-4dca-9489-5ca19a85de51.jpg',
  spiralCandle: 'https://cdn.magicpatterns.com/patterns/generated-images/d5b2134d-7284-454e-a975-e2370889ec7a.jpg',
  cupRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/c7afa25d-1b55-461f-acc0-c0b74489688f.jpg',
  balloonCar: 'https://cdn.magicpatterns.com/patterns/generated-images/1b77d90c-0891-4c79-a619-a4c81761be7a.jpg',
  dadDaughterRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/8e60ade3-a3bd-473a-bd87-1981ee785bca.jpg',
  dadDaughterCrafting: 'https://cdn.magicpatterns.com/patterns/generated-images/26993425-d121-4a8b-b522-6d7314851a4b.jpg',
  boyCrafting: 'https://cdn.magicpatterns.com/patterns/generated-images/264272c6-4177-4bef-99b9-5697d7513e4a.jpg',
  bottleRocket: 'https://cdn.magicpatterns.com/patterns/generated-images/29df76db-cefd-466b-a31e-33832ca13543.jpg',
}

export const painPoints: RichLine[] = [
  [
    { text: 'Your child spends hours ' },
    { text: 'glued to a screen', tone: 'brick' },
    { text: ' and ' },
    { text: "you don't know what to do anymore", tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: 'Every time you take the phone away, ' },
    { text: 'the meltdown begins', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You worry about ' },
    { text: 'the consequences', tone: 'navy' },
    { text: ' of seeing your child ' },
    { text: 'constantly glued to a screen', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You hand over the phone and then ' },
    { text: 'you regret it', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You love your child deeply, but ' },
    { text: "you don't feel like the parent you'd like to be", tone: 'navy' },
    { text: '.' },
  ],
]

export const includedItems: IncludedItem[] = [
  {
    title: 'Cardboard robotics projects',
    detail: 'to build robots, moving parts and simple mechanisms using everyday materials.',
  },
  {
    title: 'Science experiments',
    detail: 'with clear, visual activities using simple household ingredients and objects.',
  },
  {
    title: 'Engineering builds & vehicles',
    detail: 'including cars, structures, mechanisms and creative construction projects.',
  },
  {
    title: 'Creative crafts & hands-on builds',
    detail: 'using cardboard, paper, bottles, clay, balloons and other everyday materials.',
  },
  {
    title: 'Focus, math & motor-skill activities',
    detail: 'to learn, solve challenges and practice through play.',
  },
  {
    title: 'Instant, lifetime digital access',
    detail: 'with no subscription and no expiration date.',
  },
]

export const outcomes: RichLine[] = [
  [
    { text: "You'll turn chaotic afternoons into " },
    { text: 'moments of play, joy and connection', tone: 'forest' },
    { text: ' — with less yelling and frustration.' },
  ],
  [
    { text: 'Your child will start ' },
    { text: 'building things with their own hands', tone: 'navy' },
    { text: ' instead of immediately reaching for the phone.' },
  ],
  [
    { text: "You'll be able to " },
    { text: 'set healthier screen limits', tone: 'forest' },
    { text: ' without constant shouting, punishment or guilt afterwards.' },
  ],
  [
    { text: 'Your child will stop reaching for the phone every time boredom hits… and start ' },
    { text: 'creating out of curiosity and play', tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: "You'll discover you can parent with " },
    { text: 'fewer screens, less guilt and more creativity', tone: 'forest' },
    { text: '.' },
  ],
]

/**
 * Bonus content. Each gift is an independent entry — edit the label, title or
 * image of any single bonus here without touching the rest of the section.
 */
export const gifts: Gift[] = [
  {
    label: 'Gift 1',
    title: 'Quick guide to calm tantrums in 3 steps',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e4aca02f-a7ce-4aaa-abea-44dc103dd1bc.jpg',
  },
  {
    label: 'Gift 2',
    title: 'Editable checklist for personalized routines',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/55dffc44-2db6-4e63-b1aa-fd154b4433c0.jpg',
  },
  {
    label: 'Gift 3',
    title: 'Reward and positive behavior boards',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/86ac65b7-153d-4f04-8a3b-ccf81c196ea4.jpg',
  },
  {
    label: 'Gift 4',
    title: 'Mini guide: I survived chaotic mornings',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b4c64f62-025a-440e-8600-c9e91981a65c.jpg',
  },
  {
    label: 'Gift 5',
    title: 'Easy and fun recipes',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/57852096-975a-4d4a-8f0c-554fb354fc47.jpg',
  },
  {
    label: 'Gift 6',
    title: 'Free your child from digital addiction',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2ed14884-8e9a-47b3-8684-565d3d52d660.jpg',
  },
  {
    label: 'Gift 7',
    title: 'Magical stories for positive parenting',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/30f56630-bd86-4942-8073-a8d3d938e155.jpg',
  },
  {
    label: 'Gift 8',
    title: 'Short stories to raise kids with values',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/57709948-2ef0-42e6-ba35-6a4eee4484a7.jpg',
  },
  {
    label: 'Gift 9',
    title: 'Focus power: 101 tips for parenting with ADHD',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8b0c285d-50f2-4fcc-a85f-69d3beb986c8.jpg',
  },
  {
    label: 'Gift 10',
    title: "What I wish I'd known before becoming a mom",
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9b8cc9f8-6e57-4b3b-bf24-4ff95501ab99.jpg',
  },
  {
    label: 'Gift 11',
    title: '10 screen-free activities to reconnect as a family',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c7331948-3d7d-4ee7-90b5-2d5b7deed46b.jpg',
  },
  {
    label: 'Gift 12',
    title: '70 daily connection ideas',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0dadf26f-5c49-4c7e-b9fb-de4043b2a4fa.jpg',
  },
]

export const reviews: Review[] = [
  {
    name: 'Valentina',
    country: 'Colombia',
    rating: 5,
    text: 'I thought it would be complicated to put something together myself, but the guides are crystal clear. My son built his first robot with things we already had in the kitchen.',
  },
  {
    name: 'Camila',
    country: 'Argentina',
    rating: 5,
    text: "The best part is that I didn't have to buy anything special. Cardboard, bottle caps, and done. Forty minutes of complete silence while I work.",
  },
  {
    name: 'Sofía',
    country: 'Mexico',
    rating: 4,
    text: "Very good, although some projects took me longer than the guide said. Still worth it, my daughter didn't put the project down all afternoon.",
  },
  {
    name: 'Marcela',
    country: 'Chile',
    rating: 5,
    text: 'At first I hesitated because I thought I would have to explain everything myself, but the steps are so clear that my kids follow them alone and build everything with their hands.',
  },
  {
    name: 'Daniela',
    country: 'Peru',
    rating: 5,
    text: 'We print one project a week and it has become our Sunday plan. My two kids fight over who gets to cut the cardboard.',
  },
  {
    name: 'Rocío',
    country: 'Ecuador',
    rating: 5,
    text: 'I bought it to try it out and it ended up being what we use the most at home this month. You can tell it was designed by people who understand kids.',
  },
]

export const faqs: FaqItem[] = [
  {
    question: 'Is it physical or digital?',
    answer:
      'It is 100% digital. After your purchase you receive an email with immediate access to every project and printable activity, so there is no shipping and no waiting.',
  },
  {
    question: 'What ages is it for?',
    answer:
      'It works best between ages 4 and 12. Each activity is labeled by age and difficulty level, so you can pick something simpler or more challenging depending on the day.',
  },
  {
    question: 'Do I need special materials?',
    answer:
      'No. Almost everything is built with cardboard, bottles, caps, straws, tape and other things you already have at home. Each project lists its materials before you start.',
  },
  {
    question: 'Do I need a printer?',
    answer:
      'Some activities are designed to be printed, while others can be followed directly from a phone, tablet or computer. Each activity will clearly indicate what you need before getting started.',
  },
  {
    question: "What if I don't have time?",
    answer:
      'Most projects take between 10 and 30 minutes. You can start with a single 10-minute activity a day and still see the difference in your afternoons.',
  },
  {
    question: "What if it doesn't work for me?",
    answer:
      'You have our 7-day Hands-On Guarantee. Try the projects, and if it is not for your family, write to us and we refund you, no awkward questions.',
  },
  {
    question: 'Does the access expire?',
    answer:
      'Never. Your access is lifetime and includes every future update to the kit at no extra cost.',
  },
  {
    question: 'Is it safe to pay here?',
    answer:
      'Yes. Payments are processed through an encrypted checkout, and we never store your card details.',
  },
]
